
import { Button } from '@/components/ui/button';
import { ChevronRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { scrollToElement } from '@/utils/scrollUtils';
import CurrentDate from '@/components/CurrentDate';
import { TypingAnimation } from '@/components/ui/typing-animation';

const Hero = () => {
  const serviceTitles = [
    'Machine-as-a-Service',
    'Outcome-based Contracting',
    'Factory-as-a-Service', 
    'Performance-based Contracting',
    'Pay-per-Use',
    'Performance-based Logistics',
    'Robot-as-a-Service',
    'Results-based Contracting',
    'Performance-based Acquisition'
  ];

  return (
    <section className="relative py-20 md:py-28 lg:py-32 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-hero-glow opacity-70"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.3] bg-[length:30px_30px]"></div>
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-gradient-to-br from-accent/5 to-secondary/5 rounded-full filter blur-[120px] opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-accent/5 to-secondary/5 rounded-full filter blur-[100px] opacity-20"></div>
      
      <div 
        className="container px-4 sm:px-6 lg:px-8 mx-auto relative"
      >
        <div className="max-w-3xl mx-auto text-center">
          <CurrentDate />
          
          <div className="inline-flex items-center px-3 py-1 mb-6 mt-4 rounded-full border border-border bg-background text-foreground text-sm">
            <Sparkles size={14} className="mr-2 text-accent" />
            <span>Outcome-Based Models for Industrial Operations</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-5 text-balance text-foreground">
            Transform Your Business With 
            <span className="bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent block mt-2 leading-normal pb-1">
              <TypingAnimation
                texts={serviceTitles}
                typingDuration={100}
                pauseDuration={1200}
                erasingDuration={50}
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent"
              />
            </span>
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8 text-balance">
            Buy <span className="text-foreground font-medium">verified, measurable outcomes</span> — not assets, hours, or specs. Performance-based models, proven across robotics, IoT, and industrial operations.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            <Button className="bg-accent hover:bg-accent/90 text-white font-medium px-6 py-6 shadow-lg" asChild>
              <Link to="/consultation" className="flex items-center">
                Book a Discovery Call
                <ChevronRight size={16} className="ml-2" />
              </Link>
            </Button>
            <Button variant="outline" className="border-border py-6 hover:bg-white hover:text-accent" onClick={() => scrollToElement('workflow')}>See the Method</Button>
          </div>
          
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            <div className="glass-card p-3 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 size={18} className="text-accent mr-2 flex-shrink-0" />
                <span className="text-foreground/80 text-sm text-left">Defined Scope</span>
              </div>
            </div>
            <div className="glass-card p-3 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 size={18} className="text-accent mr-2 flex-shrink-0" />
                <span className="text-foreground/80 text-sm text-left">Measurable Outcomes</span>
              </div>
            </div>
            <div className="glass-card p-3 rounded-lg">
              <div className="flex items-center">
                <CheckCircle2 size={18} className="text-accent mr-2 flex-shrink-0" />
                <span className="text-foreground/80 text-sm text-left">Verified Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
